import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "./supabase";
import type { DriverDetailedItem, DriverListItem, DriverProfileFull } from "./types";

// Fixed roster shown on the public drivers page. Decoupled from the `driver`
// role so role assignments don't change who appears here.
const FEATURED_DRIVER_IDS = [
  "d1000001-aaaa-4000-8000-000000000001", // Dávid Tóth
  "d2000002-bbbb-4000-8000-000000000002", // Tamás Simon
  "d3000003-cccc-4000-8000-000000000003", // Norbert Kiss
  "d4000004-dddd-4000-8000-000000000004", // Balázs Palla
  "d5000005-eeee-4000-8000-000000000005", // Dániel Oláh
  "d6000006-ffff-4000-8000-000000000006", // Máté Varga
];

export const getDrivers = createServerFn({ method: "GET" }).handler(
  async (): Promise<DriverListItem[]> => {
    const db = supabaseAdmin;

    const { data, error } = await db
      .from("profiles")
      .select("id, display_name, avatar_url, irating_road, irating_oval")
      .in("id", FEATURED_DRIVER_IDS)
      .order("display_name");

    if (error) throw new Error(`Failed to fetch drivers: ${error.message}`);

    // Get setup counts per driver
    const driverIds = (data ?? []).map((d) => d.id);
    const { data: counts } = await db
      .from("setups")
      .select("driver_id")
      .eq("status", "approved")
      .in("driver_id", driverIds);

    const countMap = new Map<string, number>();
    (counts ?? []).forEach((c: any) => {
      countMap.set(c.driver_id, (countMap.get(c.driver_id) ?? 0) + 1);
    });

    return (data ?? []).map((d) => ({
      ...d,
      setup_count: countMap.get(d.id) ?? 0,
    }));
  },
);

export const getDriverProfile = createServerFn({ method: "GET" })
  .inputValidator((input: { driverId: string }) => input)
  .handler(async ({ data: { driverId } }): Promise<DriverProfileFull | null> => {
    const db = supabaseAdmin;

    // Get profile
    const { data: profile, error } = await db
      .from("profiles")
      .select(
        `id, user_id, display_name, avatar_url, bio,
         iracing_cust_id, irating_road, irating_oval,
         license_road, license_oval, achievements, social_links`,
      )
      .eq("id", driverId)
      .in("id", FEATURED_DRIVER_IDS)
      .single();

    if (error || !profile) return null;

    // Get stats + setups in parallel
    const [setupsResult, downloadsResult, seriesResult] = await Promise.all([
      // Approved setups
      db
        .from("setups")
        .select(
          `
          id, title, description, version, is_free_sample, download_count,
          created_at, updated_at, week_number,
          car:cars!inner(id, name, name_short, image_url),
          track:tracks!inner(id, name, config),
          series:series(id, name),
          season:seasons!inner(id, name),
          versions:setup_versions(version_number, change_notes, best_lap_time),
          files:setup_files(setup_type)
        `,
        )
        .eq("driver_id", driverId)
        .eq("status", "approved")
        .order("created_at", { ascending: false }),

      // Total downloads
      db.from("setups").select("download_count").eq("driver_id", driverId).eq("status", "approved"),

      // Active series count
      db
        .from("setups")
        .select("series_id")
        .eq("driver_id", driverId)
        .eq("status", "approved")
        .not("series_id", "is", null),
    ]);

    const setups = (setupsResult.data ?? []).map((s: any) => ({
      ...s,
      driver: {
        id: profile.id,
        display_name: profile.display_name,
        avatar_url: profile.avatar_url,
      },
      latest_version: s.versions?.sort(
        (a: any, b: any) => b.version_number - a.version_number,
      )[0] ?? {
        version_number: 1,
        change_notes: null,
      },
      file_types: [
        ...new Set(s.files?.filter((f: any) => f.setup_type).map((f: any) => f.setup_type)),
      ],
    }));

    const totalDownloads = (downloadsResult.data ?? []).reduce(
      (sum: number, s: any) => sum + (s.download_count ?? 0),
      0,
    );

    const uniqueSeries = new Set((seriesResult.data ?? []).map((s: any) => s.series_id));

    return {
      ...profile,
      stats: {
        total_setups: setups.length,
        total_downloads: totalDownloads,
        active_series: uniqueSeries.size,
      },
      setups,
    };
  });

export const getDriversWithProfiles = createServerFn({ method: "GET" }).handler(
  async (): Promise<DriverDetailedItem[]> => {
    const db = supabaseAdmin;

    // Get all driver profiles with full details
    const { data: profiles, error } = await db
      .from("profiles")
      .select(
        `id, user_id, display_name, avatar_url, bio,
         iracing_cust_id, irating_road, irating_oval,
         license_road, license_oval, achievements, social_links`,
      )
      .in("id", FEATURED_DRIVER_IDS)
      .order("display_name");

    if (error) throw new Error(`Failed to fetch drivers: ${error.message}`);

    const driverIds = (profiles ?? []).map((d) => d.id);

    // Get all approved setups for all drivers (for stats + recent setups)
    const { data: allSetups } = await db
      .from("setups")
      .select(
        `id, title, driver_id, download_count, created_at,
         car:cars!inner(name),
         track:tracks!inner(name),
         files:setup_files(setup_type)`,
      )
      .eq("status", "approved")
      .in("driver_id", driverIds)
      .order("created_at", { ascending: false });

    // Build per-driver data
    const setupsByDriver = new Map<string, typeof allSetups>();
    (allSetups ?? []).forEach((s: any) => {
      const existing = setupsByDriver.get(s.driver_id) ?? [];
      existing.push(s);
      setupsByDriver.set(s.driver_id, existing);
    });

    return (profiles ?? []).map((profile) => {
      const driverSetups = setupsByDriver.get(profile.id) ?? [];
      const totalDownloads = driverSetups.reduce(
        (sum: number, s: any) => sum + (s.download_count ?? 0),
        0,
      );

      const recentSetups = driverSetups.slice(0, 3).map((s: any) => ({
        id: s.id,
        title: s.title,
        car_name: s.car?.name ?? "Unknown Car",
        track_name: s.track?.name ?? "Unknown Track",
        setup_types: [
          ...new Set(s.files?.filter((f: any) => f.setup_type).map((f: any) => f.setup_type)),
        ] as string[],
      }));

      return {
        ...profile,
        achievements: profile.achievements ?? [],
        social_links: profile.social_links ?? {},
        stats: {
          total_setups: driverSetups.length,
          total_downloads: totalDownloads,
        },
        recent_setups: recentSetups,
      };
    });
  },
);
